/**
 * Inventory + service-link data layer.
 *
 * The custom-fields hooks live in `service-detail.ts`; this file owns the
 * inventory-side queries + mutations: the items catalogue itself, plus the
 * `service_inventory_links` join used to wire tier / add-on options to a
 * specific service.
 *
 * Endpoints not in the OpenAPI codegen yet are hand-rolled via
 * `authFetchJson` so the session-expired bouncer still fires on 401s.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authFetch, authFetchJson } from "./auth-fetch";
import { throwIfNotOk } from "./api-error";
import { useAuthStore } from "./auth";

export type InventoryKind = "plan" | "physical" | "rental" | "addon";

/** Only PHYSICAL + RENTAL track stock; PLAN tiers and ADDON extras are
 *  conceptually "always available". Mirrors the same predicate on the
 *  backend (`InventoryController.hasStock`). */
export function hasStock(kind: string): boolean {
  return kind === "physical" || kind === "rental";
}

/** Friendly labels for the Kind picker + display surfaces. Keeping
 *  labels here so they're consistent across Inventory list, Item
 *  panel, and any future Settings page. */
export const KIND_LABELS: Record<InventoryKind, string> = {
  plan: "Plan",
  physical: "Physical",
  rental: "Rental",
  addon: "Add-on",
};

export const KIND_DESCRIPTIONS: Record<InventoryKind, string> = {
  plan: "Service tier (e.g. Small / Medium / Large package). Customer picks one.",
  physical: "Stocked good (e.g. gowns, cameras). Decrements on use.",
  rental: "Stocked, lent + returned (rental fleet, equipment).",
  addon: "Non-stocked extra (service fees, upgrades, paper bag).",
};

export type InventoryItem = {
  id: string;
  kind: InventoryKind;
  category: string | null;
  sku: string | null;
  name: string;
  description: string | null;
  unit: string | null;
  unitPrice: number | null;
  currency: string | null;
  qtyOnHand: number;
  costPerUnit: number | null;
  active: boolean;
};

/**
 * Tenant-wide custom-field value attached to an inventory item. Backend
 * returns the full schema (including fields the item hasn't filled in
 * yet — `value` is null) so the panel can render empty inputs without
 * a second roundtrip. Same shape as `CustomerCustomFieldValue` in
 * `lib/customers.ts`; kept distinct so type-renames stay scope-local.
 */
export type InventoryCustomFieldValue = {
  fieldId: string;
  label: string;
  type: string;
  required: boolean;
  options?: unknown;
  value: string | null;
};

/**
 * Detail shape returned by GET /api/v1/inventory/items/{id}. Equivalent
 * to `InventoryItem` plus the embedded `customFieldValues` array. The
 * list endpoint stays slim — only the panel pulls the detail.
 */
export type InventoryItemDetail = InventoryItem & {
  customFieldValues: InventoryCustomFieldValue[];
};

export type InventoryOverview = {
  items: InventoryItem[];
};

export function useInventory(opts?: { kind?: InventoryKind; category?: string }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["inventory", opts?.kind ?? "all", opts?.category ?? "all"],
    enabled: !!accessToken,
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (opts?.kind) qs.set("kind", opts.kind);
      if (opts?.category) qs.set("category", opts.category);
      const suffix = qs.size ? `?${qs.toString()}` : "";
      return authFetchJson<InventoryOverview>(`/api/v1/inventory${suffix}`);
    },
  });
}

export type CreateInventoryItem = {
  name: string;
  kind: InventoryKind;
  category?: string;
  sku?: string;
  description?: string;
  unitPrice?: number;
  currency?: string;
  qtyOnHand?: number;
  /** Custom-field values to populate at create time. */
  customFieldValues?: { fieldId: string; value: string | null }[];
};

export function useCreateInventoryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateInventoryItem) =>
      authFetchJson<InventoryItemDetail>("/api/v1/inventory/items", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inventory"] }),
  });
}

// --- Service ↔ Inventory links --------------------------------------------

export type InventoryLinkRole = "tier" | "addon";

export type LinkItem = {
  id: string;
  name: string;
  kind: InventoryKind;
  category: string | null;
  unitPrice: number | null;
  currency: string | null;
  qtyOnHand: number;
};

/**
 * Resolved link returned by GET /services/{id}/inventory — both item-links
 * and category-links flatten to one row per item the customer can pick.
 * `viaCategory` is set when the row was resolved from a category-link.
 */
export type ServiceInventoryLink = {
  linkId: string;
  role: InventoryLinkRole;
  required: boolean;
  orderIndex: number;
  viaCategory: string | null;
  item: LinkItem;
};

/**
 * Management shape returned by GET /services/{id}/inventory/links — keeps
 * category-links un-flattened so the operator can edit their exclusion set.
 */
export type ManagedInventoryLink =
  | {
      linkId: string;
      subjectKind: "item";
      role: InventoryLinkRole;
      required: boolean;
      orderIndex: number;
      item: LinkItem;
      category: null;
      excludedItemIds: string[];
      previewItems: LinkItem[];
    }
  | {
      linkId: string;
      subjectKind: "category";
      role: InventoryLinkRole;
      required: boolean;
      orderIndex: number;
      item: null;
      category: string;
      excludedItemIds: string[];
      previewItems: LinkItem[];
    };

export function useServiceInventory(serviceId: string | null | undefined) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["service.inventory", serviceId],
    enabled: !!accessToken && !!serviceId,
    queryFn: () =>
      authFetchJson<ServiceInventoryLink[]>(
        `/api/v1/services/${serviceId}/inventory`,
      ),
  });
}

export function useManagedServiceInventory(serviceId: string | null | undefined) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["service.inventory.managed", serviceId],
    enabled: !!accessToken && !!serviceId,
    queryFn: () =>
      authFetchJson<ManagedInventoryLink[]>(
        `/api/v1/services/${serviceId}/inventory/links`,
      ),
  });
}

export type LinkInventoryBody =
  | {
      subjectKind: "item";
      itemId: string;
      role: InventoryLinkRole;
      required?: boolean;
    }
  | {
      subjectKind: "category";
      category: string;
      role: InventoryLinkRole;
      required?: boolean;
      excludeItemIds?: string[];
    };

export async function linkServiceInventory(
  serviceId: string,
  body: LinkInventoryBody,
): Promise<ManagedInventoryLink> {
  return authFetchJson<ManagedInventoryLink>(
    `/api/v1/services/${serviceId}/inventory`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function patchServiceInventoryLink(
  serviceId: string,
  linkId: string,
  body: {
    role?: InventoryLinkRole;
    required?: boolean;
    excludeItemIds?: string[];
  },
): Promise<ManagedInventoryLink> {
  return authFetchJson<ManagedInventoryLink>(
    `/api/v1/services/${serviceId}/inventory/${linkId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export function useInventoryCategories(kind?: InventoryKind) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["inventory.categories", kind ?? "all"],
    enabled: !!accessToken,
    queryFn: () => {
      const qs = kind ? `?kind=${kind}` : "";
      return authFetchJson<string[]>(`/api/v1/inventory/categories${qs}`);
    },
  });
}

export type InventoryCategoryRecord = { id: string; name: string };

/**
 * Creates a category registry row so the category-first flow can land on
 * an empty category page that survives reloads. Idempotent server-side:
 * posting an existing name returns the existing row.
 */
export function useCreateInventoryCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      authFetchJson<InventoryCategoryRecord>("/api/v1/inventory/categories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inventory.categories"] }),
  });
}

export async function unlinkServiceInventory(
  serviceId: string,
  linkId: string,
): Promise<void> {
  const res = await authFetch(
    `/api/v1/services/${serviceId}/inventory/${linkId}`,
    { method: "DELETE" },
  );
  await throwIfNotOk(res);
}

// --- Item PATCH + movements (Inventory Item Detail panel) -----------------

export type InventoryMovement = {
  id: string;
  itemId: string;
  movementType: "stock_in" | "stock_out" | "adjust" | "usage";
  qtyDelta: number;
  bookingId: string | null;
  note: string | null;
  createdAt: string;
};

export type PatchInventoryItem = {
  name?: string;
  kind?: InventoryKind;
  category?: string;
  description?: string;
  unitPrice?: number;
  currency?: string;
  costPerUnit?: number;
  active?: boolean;
  /** Upsert per row; blank values delete the existing row. */
  customFieldValues?: { fieldId: string; value: string | null }[];
};

export function useInventoryItemDetail(itemId: string | null | undefined) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["inventory.item", itemId],
    enabled: !!accessToken && !!itemId,
    queryFn: () =>
      authFetchJson<InventoryItemDetail>(`/api/v1/inventory/items/${itemId}`),
  });
}

export function usePatchInventoryItem(itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchInventoryItem) =>
      authFetchJson<InventoryItemDetail>(`/api/v1/inventory/items/${itemId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["inventory.item", itemId] });
      qc.invalidateQueries({ queryKey: ["inventory.movements", itemId] });
    },
  });
}

export function useInventoryMovements(itemId: string | null | undefined) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["inventory.movements", itemId],
    enabled: !!accessToken && !!itemId,
    queryFn: () =>
      authFetchJson<InventoryMovement[]>(
        `/api/v1/inventory/movements?itemId=${itemId}`,
      ),
  });
}

export type CreateMovementInput = {
  itemId: string;
  movementType: "stock_in" | "stock_out" | "adjust";
  qty: number;
  note?: string;
};

export function useStockMovement(itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMovementInput) =>
      authFetchJson<InventoryMovement>("/api/v1/inventory/movements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["inventory.movements", itemId] });
    },
  });
}
